import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { DueNow } from "@/components/Layout/DueNow/DueNow";
import {
  useBillingPeriod,
  useGetFlatPricesAndName,
  useHasVoiceOption,
} from "@/hooks/useDataContextHooks";
import { useGetCampaignEndDate } from "@/hooks/useGetCampaignEndDate";
import { DataContext } from "@/provider/DataContextProvider";
import { BasketEntry } from "@/services/model";
import { getCreditAmount } from "@/utils/basketUtils";
import { getEntry } from "@/utils/dataContextHelpers";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { getNextPaymentDate } from "@/utils/tariffUtils";
import { getCurrencyFromLookupValue } from "@/utils/translationHelpers";

/** Renders the payment due now box of the checkout page */
export const CheckoutDueNow: FCC = () => {
  const { dataContext } = useContext(DataContext);

  const { t, i18n } = useTranslation();
  const currency = getCurrencyFromLookupValue(
    dataContext.basket?.currency || { id: 1 },
  );

  const hasVoiceOption = useHasVoiceOption();

  const entry = getEntry(dataContext) as BasketEntry;
  const isESim = entry?.esim;

  // flat name and flat price
  const {
    flatName,
    flatPrice,
    voicePrice,
    onceTotal,
    onceSubtotal,
    discountAmount,
    discountName,
  } = useGetFlatPricesAndName();
  const { campaignEndDate } = useGetCampaignEndDate(dataContext.basket);

  const credit = getCreditAmount(dataContext.basket?.balance);
  const creditTimeRemaining =
    dataContext.basket?.total?.oneTime?.amountDiscounts || 0;
  // Align with tariff-change / Watch SIM: subtract discount (already in onceTotal)
  // and credit; floor at 0 so over-100% credit shows CHF 0.00.
  const totalOnce = Math.max((onceTotal || 0) - credit, 0);
  const recurringAmount = dataContext.basket?.total?.recurring?.amount;

  const billingPeriod = useBillingPeriod();
  const billingPeriodText = t(
    "Onboarding.sections.checkout.summary.billingPeriod",
    {
      days: billingPeriod,
    },
  );
  // Same structure as tariff-change SubscriptionDueNow:
  // "Total" + "Today", then "Thereafter" + "due on" + amount + interval.
  const recurringCaption = t(
    "portal:cockpit.managementTile.watchSim.checkout.today",
  );
  const recurringDate = t("Common.checkout.dueOn", {
    date: getLocalizedDateString(
      getNextPaymentDate(billingPeriod),
      i18n.language,
    ),
  });
  const recurringBillingPeriod = t(
    "portal:cockpit.managementTile.tariff.payInterval",
    { days: billingPeriod },
  );

  return (
    <DueNow
      billingPeriod={billingPeriodText}
      recurringCaption={recurringCaption}
      recurringAmount={recurringAmount}
      recurringDate={recurringDate}
      recurringBillingPeriod={recurringBillingPeriod}
      hideSubtotal
      dataRateName={flatName}
      dataRatePrice={flatPrice}
      currency={currency}
      totalOnce={totalOnce}
      subtotalOnce={onceSubtotal}
      discountAmount={discountAmount}
      discountName={discountName}
      discountEndDate={campaignEndDate}
      credit={credit}
      creditTimeRemaining={creditTimeRemaining}
      isPrecharged={!isESim}
      {...(hasVoiceOption
        ? {
            voiceOptionName: t("portal:common.device.attributes.voiceOption"),
            voiceOptionPrice: voicePrice,
          }
        : {})}
    />
  );
};
